'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface Props {
	children: React.ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// Log error to your error tracking service
		console.error('Error caught by boundary:', error, errorInfo);

		// Show error toast to user
		toast.error('Something went wrong. Please try refreshing the page.');
	}

	handleReset = () => {
		this.setState({ hasError: false, error: null });
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-background">
					<div className="max-w-md w-full p-6 bg-card rounded-lg shadow-lg">
						<h2 className="text-2xl font-bold text-foreground mb-4">Oops! Something went wrong</h2>
						<p className="text-muted-foreground mb-4">
							We apologize for the inconvenience. Please try refreshing the page or contact support
							if the problem persists.
						</p>
						<div className="space-y-4">
							<Button onClick={this.handleReset} className="w-full">
								Refresh Page
							</Button>
							<Button
								variant="outline"
								className="w-full"
								onClick={() => (window.location.href = '/')}
							>
								Go to Homepage
							</Button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
